<template>
    <div class="comment-card">
      <div class="comment-hd">
        <div class="comment-avatar">
          <img v-if="comment.userProfileImageURL" :src="comment.userProfileImageURL" :alt="comment.userName" />
          <span v-else>{{ (comment.userName || '?').charAt(0) }}</span>
        </div>
        <div class="comment-meta">
          <p class="comment-author">{{ comment.userName || 'Anonymous' }}</p>
          <p class="comment-time">{{ formatTimestamp(comment.timestamp) }}</p>
        </div>
      </div>

      <p class="comment-text">{{ comment.text }}</p>

      <div class="comment-actions">
        <button class="comment-action-btn" @click="toggleLike">
          <svg viewBox="0 0 24 24" :fill="comment.likes?.includes(userUID) ? 'rgba(210,175,100,0.3)' : 'none'" stroke="currentColor" stroke-width="1.5">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
          </svg>
          <span>{{ comment.likes?.length || 0 }}</span>
        </button>
        <button class="comment-action-btn" @click="replying = !replying">Reply</button>
      </div>

      <!-- Reply box -->
      <div v-if="replying" class="reply-box">
        <textarea v-model="replyText" class="reply-input" placeholder="Write a reply…" rows="2"></textarea>
        <button class="reply-submit" :disabled="!replyText.trim()" @click="submitReply">Post Reply</button>
      </div>

      <!-- Nested replies -->
      <div v-if="children.length" class="comment-children">
        <CommentThread
          v-for="child in children"
          :key="child.id"
          :comment="child"
          :post-id="postID"
          :parent-path="fullPath + '/replies'"
        />
      </div>
    </div>
  </template>
  
  <script>
  import {
    doc,
    getDoc,
    addDoc,
    getDocs,
    collection,
    updateDoc,
    arrayUnion,
    arrayRemove,
  } from "firebase/firestore";
  import { db, auth } from "@/firebase";
  
  export default {
    name: "CommentThread",
    // No import/registration needed for the recursive <CommentThread> used
    // in this component's own template — Vue resolves that automatically
    // via the `name` option above.
    props: {
      comment: Object,
      postID: String,
      parentPath: String, // e.g., posts/postID/comments
    },
    data() {
      return {
        userUID: auth.currentUser?.uid || "",
        replying: false,
        replyText: "",
        children: [],
        defaultUserImage: "/default-user.jpg",
      };
    },
    computed: {
      fullPath() {
        return `${this.parentPath}/${this.comment.id}`;
      },
    },
    methods: {
      formatTimestamp(ts) {
        return ts?.seconds
          ? new Date(ts.seconds * 1000).toLocaleString()
          : "just now";
      },
      async toggleLike() {
        try {
          const ref = doc(db, this.fullPath);
          const newLikes = this.comment.likes?.includes(this.userUID)
            ? arrayRemove(this.userUID)
            : arrayUnion(this.userUID);
          await updateDoc(ref, { likes: newLikes });
          this.loadReplies();
        } catch (err) {
          console.error("Like failed:", err);
        }
      },
      async submitReply() {
        const user = auth.currentUser;
        if (!user || !this.replyText) {
          console.warn("Missing user or reply text");
          return;
        }
  
        try {
          const userRef = doc(db, "user", user.uid);
          const userSnap = await getDoc(userRef);
          const userData = userSnap.exists() ? userSnap.data() : {};
  
          const reply = {
            userID: user.uid,
            userName: userData.fullName || "Anonymous",
            userProfileImageURL: userData.image || this.defaultUserImage,
            text: this.replyText,
            likes: [],
            timestamp: new Date(),
          };
  
          const replyRef = collection(db, `${this.fullPath}/replies`);
          await addDoc(replyRef, reply);
          this.replyText = "";
          this.replying = false;
          this.loadReplies();
        } catch (err) {
          console.error("Reply failed:", err);
        }
      },
      async loadReplies() {
        try {
          const repliesRef = collection(db, `${this.fullPath}/replies`);
          const snap = await getDocs(repliesRef);
          this.children = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        } catch (err) {
          console.error("Loading replies failed:", err);
        }
      },
    },
    mounted() {
      this.loadReplies();
    },
  };
  </script>

  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

  .comment-card {
    border: 1px solid rgba(210,175,100,0.1);
    background: rgba(210,175,100,0.02);
    padding: 16px 18px;
    margin-bottom: 4px;
    font-family: 'Montserrat', sans-serif;
  }
  .comment-hd { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
  .comment-avatar {
    width: 30px; height: 30px; border-radius: 50%; overflow: hidden; flex-shrink: 0;
    border: 1px solid rgba(210,175,100,0.2); background: rgba(210,175,100,0.05);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Cormorant', serif; font-size: 13px; font-weight: 300; color: rgba(210,175,100,0.5);
  }
  .comment-avatar img { width: 100%; height: 100%; object-fit: cover; }
  .comment-author { font-family: 'Cormorant', serif; font-size: 15px; font-weight: 400; color: #edeae2; line-height: 1.1; }
  .comment-time { font-size: 8px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(237,232,223,0.2); margin-top: 2px; }
  .comment-text { font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.6); line-height: 1.65; margin: 0 0 10px 40px; }
  .comment-actions { display: flex; align-items: center; gap: 16px; margin-left: 40px; }
  .comment-action-btn {
    display: flex; align-items: center; gap: 6px; background: none; border: none; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase;
    color: rgba(237,232,223,0.25); transition: color 0.2s; padding: 0;
  }
  .comment-action-btn:hover { color: rgba(210,175,100,0.7); }
  .comment-action-btn svg { width: 13px; height: 13px; }
  .reply-box { margin: 12px 0 4px 40px; display: flex; flex-direction: column; gap: 8px; }
  .reply-input {
    background: transparent; border: 1px solid rgba(210,175,100,0.15); padding: 10px;
    color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 12.5px; font-weight: 300;
    outline: none; caret-color: rgba(210,175,100,0.8); resize: vertical; transition: border-color 0.2s;
  }
  .reply-input::placeholder { color: rgba(237,232,223,0.2); font-style: italic; }
  .reply-input:focus { border-color: rgba(210,175,100,0.4); }
  .reply-submit {
    align-self: flex-end; font-family: 'Montserrat', sans-serif; font-size: 8.5px; font-weight: 600;
    letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.8);
    background: rgba(210,175,100,0.08); border: 1px solid rgba(210,175,100,0.4);
    padding: 8px 14px; cursor: pointer; transition: background 0.2s, opacity 0.2s;
  }
  .reply-submit:hover:not(:disabled) { background: rgba(210,175,100,0.16); }
  .reply-submit:disabled { opacity: 0.3; cursor: not-allowed; }
  .comment-children { margin: 10px 0 0 24px; padding-left: 16px; border-left: 1px solid rgba(210,175,100,0.08); display: flex; flex-direction: column; gap: 4px; }
  </style>